import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { smsTemplatesApi } from '../services/api'
import { showError } from '../utils/showError'
import { extractErrorMessage } from '../utils/errorUtils'
import SendSmsTestModal from '../components/SendSmsTestModal'
import SmsBatchGenerateModal from '../components/SmsBatchGenerateModal'
import {
  PageHeader,
  Button,
  LoadingSpinner,
  EmptyState,
  ErrorMessage,
  ListContainer,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  Badge,
  Card,
  CardBody
} from '../components/common'

const SMS_TEMPLATE_CATEGORIES = {
  'Authentication': ['SignUp', 'ForgotPassword', 'ResetPassword'],
  'Welcome & Account': ['WelcomeMessage', 'BuildingInvitation'],
  'Notifications': ['Announcement', 'DeliveryNotification', 'Reminder'],
  'Amenities': ['AmenityBookingConfirmation', 'AmenityBookingCancellation', 'AdminAmenityBookingRequest'],
  'Parking': ['ParkingBookingConfirmation', 'ParkingBookingCancellation', 'AdminParkingBookingRequest'],
  'System': ['TestTemplate']
}

function getVariableCoverage(messageBody, variablesList) {
  if (!messageBody || !variablesList || variablesList.length === 0) return null
  const usedVars = [...messageBody.matchAll(/\{\{(\w+)\}\}/g)].map(m => m[1])
  const required = variablesList.filter(v => v.isRequired).map(v => v.name)
  const missingRequired = required.filter(name => !usedVars.includes(name))
  return {
    usedVars,
    requiredCount: required.length,
    missingRequired,
    allRequiredCovered: missingRequired.length === 0
  }
}

const SmsTemplates = () => {
  const navigate = useNavigate()
  const [templates, setTemplates] = useState([])
  const [templateTypes, setTemplateTypes] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showSendTest, setShowSendTest] = useState(false)
  const [testingTemplate, setTestingTemplate] = useState(null)
  const [activeTab, setActiveTab] = useState('all-types')
  const [showBatchModal, setShowBatchModal] = useState(false)
  const [variablesMap, setVariablesMap] = useState({})

  useEffect(() => {
    loadTemplates()
    loadTemplateTypes()
  }, [])

  const loadTemplates = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await smsTemplatesApi.list()
      setTemplates(Array.isArray(response.data) ? response.data : [])
    } catch (err) {
      setError(extractErrorMessage(err))
      setTemplates([])
    } finally {
      setLoading(false)
    }
  }

  const loadTemplateTypes = async () => {
    try {
      const response = await smsTemplatesApi.getTypes()
      const types = Array.isArray(response.data) ? response.data : []
      setTemplateTypes(types)
      loadAllVariables(types)
    } catch {
      setTemplateTypes([])
    }
  }

  const loadAllVariables = async (types) => {
    const results = await Promise.allSettled(
      types.map(type =>
        smsTemplatesApi.getVariablesByType(type.value).then(res => {
          let vars = []
          if (res.data && Array.isArray(res.data.variables)) {
            vars = res.data.variables
          } else if (Array.isArray(res.data)) {
            vars = res.data
          }
          return { typeValue: type.value, variables: vars }
        })
      )
    )
    const map = {}
    for (const r of results) {
      if (r.status === 'fulfilled') {
        map[r.value.typeValue] = r.value.variables
      }
    }
    setVariablesMap(map)
  }

  const getTemplateTypeName = (value) => {
    const type = templateTypes.find(t => t.value === parseInt(value))
    return type ? type.name : `Type ${value}`
  }

  const isTemplateConfigured = (typeName) => {
    const type = templateTypes.find(t => t.name === typeName)
    if (!type) return false
    return templates.some(t => parseInt(t.templateType) === type.value)
  }

  const getTemplateByTypeName = (typeName) => {
    const type = templateTypes.find(t => t.name === typeName)
    if (!type) return null
    return templates.find(t => parseInt(t.templateType) === type.value)
  }

  const handleEditTemplate = (templateType) => {
    navigate(`/sms-templates/edit/${templateType}`)
  }

  const handleCreateNew = () => {
    navigate('/sms-templates/new')
  }

  const handleEditByTypeName = (typeName) => {
    const type = templateTypes.find(t => t.name === typeName)
    if (!type) return
    const existing = templates.find(t => parseInt(t.templateType) === type.value)
    if (existing) {
      navigate(`/sms-templates/edit/${type.value}`)
    } else {
      navigate(`/sms-templates/new?type=${type.value}`)
    }
  }

  const handleDeleteTemplate = async (templateType) => {
    if (window.confirm('Are you sure you want to delete this template?')) {
      try {
        await smsTemplatesApi.delete(templateType)
        loadTemplates()
      } catch (err) {
        showError(err)
      }
    }
  }

  const handleSendTest = (template) => {
    setTestingTemplate(template)
    setShowSendTest(true)
  }

  const handleSendTestCancel = () => {
    setShowSendTest(false)
    setTestingTemplate(null)
  }

  const tabs = [
    { id: 'all-types', label: 'All Template Types' },
    { id: 'configured', label: `Configured Templates (${templates.length})` }
  ]

  if (loading) {
    return <LoadingSpinner />
  }

  // Shared: categorized cards section
  const renderCategorizedCards = () => (
    <div className="space-y-8">
      {Object.entries(SMS_TEMPLATE_CATEGORIES).map(([category, typeNames]) => {
        const categoryTypes = typeNames.filter(name =>
          templateTypes.some(t => t.name === name)
        )
        if (categoryTypes.length === 0) return null

        return (
          <div key={category}>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{category}</h3>
            <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))' }}>
              {categoryTypes.map(typeName => {
                const configured = isTemplateConfigured(typeName)
                const type = templateTypes.find(t => t.name === typeName)
                const vars = type ? variablesMap[type.value] : null
                const template = configured ? getTemplateByTypeName(typeName) : null
                const coverage = template && vars ? getVariableCoverage(template.messageBody, vars) : null
                const requiredCount = vars ? vars.filter(v => v.isRequired).length : 0
                const optionalCount = vars ? vars.filter(v => !v.isRequired).length : 0

                return (
                  <Card
                    key={typeName}
                    className="cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => handleEditByTypeName(typeName)}
                  >
                    <CardBody>
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="text-sm font-semibold text-gray-900">{typeName}</h4>
                        <Badge variant={configured ? 'accent' : 'warning'} size="small">
                          {configured ? 'Configured' : 'Not Set'}
                        </Badge>
                      </div>
                      <p className="text-xs text-gray-500 mb-2">
                        {typeName.replace(/([A-Z])/g, ' $1').trim()} SMS template
                      </p>
                      {vars && vars.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {requiredCount > 0 && (
                            <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-red-50 text-red-700">
                              {requiredCount} required
                            </span>
                          )}
                          {optionalCount > 0 && (
                            <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                              {optionalCount} optional
                            </span>
                          )}
                          {coverage && (
                            coverage.allRequiredCovered ? (
                              <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-green-50 text-green-700">
                                All required covered
                              </span>
                            ) : (
                              <span
                                className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-red-50 text-red-700"
                                title={`Missing: ${coverage.missingRequired.join(', ')}`}
                              >
                                Missing {coverage.missingRequired.length} required
                              </span>
                            )
                          )}
                        </div>
                      )}
                    </CardBody>
                  </Card>
                )
              })}
            </div>
          </div>
        )
      })}

      {/* Uncategorized types */}
      {(() => {
        const categorized = new Set(Object.values(SMS_TEMPLATE_CATEGORIES).flat())
        const uncategorized = templateTypes.filter(t => !categorized.has(t.name))
        if (uncategorized.length === 0) return null
        return (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Other</h3>
            <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))' }}>
              {uncategorized.map(type => {
                const configured = templates.some(t => parseInt(t.templateType) === type.value)
                const vars = variablesMap[type.value]
                const template = configured ? templates.find(t => parseInt(t.templateType) === type.value) : null
                const coverage = template && vars ? getVariableCoverage(template.messageBody, vars) : null
                const requiredCount = vars ? vars.filter(v => v.isRequired).length : 0
                const optionalCount = vars ? vars.filter(v => !v.isRequired).length : 0

                return (
                  <Card
                    key={type.name}
                    className="cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => handleEditByTypeName(type.name)}
                  >
                    <CardBody>
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="text-sm font-semibold text-gray-900">{type.name}</h4>
                        <Badge variant={configured ? 'accent' : 'warning'} size="small">
                          {configured ? 'Configured' : 'Not Set'}
                        </Badge>
                      </div>
                      <p className="text-xs text-gray-500 mb-2">
                        {type.name.replace(/([A-Z])/g, ' $1').trim()} SMS template
                      </p>
                      {vars && vars.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {requiredCount > 0 && (
                            <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-red-50 text-red-700">
                              {requiredCount} required
                            </span>
                          )}
                          {optionalCount > 0 && (
                            <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                              {optionalCount} optional
                            </span>
                          )}
                          {coverage && (
                            coverage.allRequiredCovered ? (
                              <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-green-50 text-green-700">
                                All required covered
                              </span>
                            ) : (
                              <span
                                className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-red-50 text-red-700"
                                title={`Missing: ${coverage.missingRequired.join(', ')}`}
                              >
                                Missing {coverage.missingRequired.length} required
                              </span>
                            )
                          )}
                        </div>
                      )}
                    </CardBody>
                  </Card>
                )
              })}
            </div>
          </div>
        )
      })()}
    </div>
  )

  // Shared: configured templates table
  const renderConfiguredTable = () => (
    <ListContainer>
      <div className="mb-4 flex justify-end">
        <Button variant="primary" onClick={handleCreateNew}>Create Template</Button>
      </div>
      {!Array.isArray(templates) || templates.length === 0 ? (
        <EmptyState
          message="No templates found"
          icon="📱"
          action={
            <Button
              variant="primary"
              onClick={handleCreateNew}
            >
              Create First Template
            </Button>
          }
        />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableCell header>Template Type</TableCell>
              <TableCell header>Template Name</TableCell>
              <TableCell header>Message</TableCell>
              <TableCell header>Length</TableCell>
              <TableCell header>Variables</TableCell>
              <TableCell header>Status</TableCell>
              <TableCell header>
                <span className="sr-only">Actions</span>
              </TableCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {templates.map((template) => {
              const vars = variablesMap[parseInt(template.templateType)]
              const coverage = vars ? getVariableCoverage(template.messageBody, vars) : null

              return (
                <TableRow key={template.templateType} clickable onClick={() => handleEditTemplate(template.templateType)}>
                  <TableCell>
                    <div className="text-sm font-medium text-gray-900">
                      {getTemplateTypeName(template.templateType)}
                    </div>
                    <div className="text-sm text-gray-500">
                      Type ID: {template.templateType}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-gray-900">
                      {template.templateName || 'Unnamed template'}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-gray-900 max-w-xs truncate">
                      {template.messageBody || 'No message'}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-gray-900">
                      {template.messageBody?.length || 0} chars
                    </div>
                    <div className="text-xs text-gray-500">
                      {template.messageBody?.length <= 160 ? '1 SMS' : `${Math.ceil(template.messageBody?.length / 160)} SMS`}
                    </div>
                  </TableCell>
                  <TableCell>
                    {coverage ? (
                      <Badge
                        variant={coverage.allRequiredCovered ? 'accent' : 'danger'}
                        size="small"
                        title={
                          coverage.allRequiredCovered
                            ? 'All required variables are used'
                            : `Missing: ${coverage.missingRequired.join(', ')}`
                        }
                      >
                        {coverage.usedVars.length}/{coverage.requiredCount} required
                      </Badge>
                    ) : vars && vars.length === 0 ? (
                      <span className="text-xs text-gray-400">No variables</span>
                    ) : (
                      <span className="text-xs text-gray-400">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={template.templateName && template.messageBody ? 'accent' : 'warning'} size="small">
                      {template.templateName && template.messageBody ? 'Complete' : 'Incomplete'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex space-x-2">
                      <Button
                        variant="ghost"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleSendTest(template)
                        }}
                        title="Send test SMS"
                      >
                        Send Test
                      </Button>
                      <Button
                        variant="danger"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDeleteTemplate(template.templateType)
                        }}
                        title="Delete template"
                      >
                        Delete
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      )}
    </ListContainer>
  )

  return (
    <div className="space-y-6">
      <PageHeader
        title="SMS Templates"
        subtitle="Manage SMS templates for the system"
        action={{
          label: "Batch Generate",
          onClick: () => setShowBatchModal(true)
        }}
      />

      {error && (
        <ErrorMessage
          message={error}
          onDismiss={() => setError(null)}
        />
      )}

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-0 -mb-px">
          {tabs.map(tab => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab 1: All Template Types */}
      {activeTab === 'all-types' && renderCategorizedCards()}

      {/* Tab 2: Configured Templates */}
      {activeTab === 'configured' && renderConfiguredTable()}

      {/* Send Test SMS Modal */}
      {showSendTest && (
        <SendSmsTestModal
          template={testingTemplate}
          templateTypeName={getTemplateTypeName(testingTemplate?.templateType)}
          onCancel={handleSendTestCancel}
        />
      )}

      {/* Batch Generate Modal */}
      {showBatchModal && (
        <SmsBatchGenerateModal
          templateTypes={templateTypes}
          existingTemplates={templates}
          variablesMap={variablesMap}
          onClose={() => setShowBatchModal(false)}
          onComplete={() => loadTemplates()}
        />
      )}
    </div>
  )
}

export default SmsTemplates
